import {
  parseMetadata,
  formatMetadataHeaders,
  applyMetadataToLrc
} from '../core/metadata';
import type { LrcMetadata } from '../core/types';
import { getEditorValue, setEditorValue } from './editor';
import { triggerHaptic } from './haptics';
import { setHistorySnapshot, showToast } from './toast';
import { updateCounters } from './app';
import { escapeHtml } from './dom';

const FIELD_IDS = ['ti', 'ar', 'al', 'au', 'length', 'offset', 'by'] as const;

/**
 * Reads all input fields inside the Metadata Modal and returns an LrcMetadata object.
 */
export function getMetadataFromFields(): LrcMetadata {
  const metadata: LrcMetadata = {};
  for (const id of FIELD_IDS) {
    const el = document.getElementById(`meta_${id}`) as HTMLInputElement | null;
    if (el && el.value.trim()) {
      metadata[id] = el.value.trim();
    }
  }
  return metadata;
}

/**
 * Populates modal form fields from an LrcMetadata object.
 */
export function setMetadataToFields(metadata: LrcMetadata): void {
  for (const id of FIELD_IDS) {
    const el = document.getElementById(`meta_${id}`) as HTMLInputElement | null;
    if (el) {
      el.value = metadata[id] || '';
      el.scrollLeft = 0;
      try {
        el.setSelectionRange(0, 0);
      } catch {}
    }
  }
  updateLivePreview();
}

/**
 * Updates the live header code preview inside the modal and the tag count badge.
 */
export function updateLivePreview(): void {
  const meta = getMetadataFromFields();
  const headers = formatMetadataHeaders(meta);
  const previewBox = document.getElementById('metaPreviewBox');
  const previewCount = document.getElementById('metaPreviewCount');

  if (previewCount) {
    previewCount.textContent = `${headers.length} tag${headers.length === 1 ? '' : 's'}`;
  }

  if (previewBox) {
    if (headers.length > 0) {
      const htmlLines = headers.map((line) => {
        const metaMatch = line.match(/^(\s*\[)([a-zA-Z]{2,10}):(.*?)(\]\s*)$/);
        if (metaMatch && metaMatch[1] && metaMatch[2] && metaMatch[3] !== undefined && metaMatch[4]) {
          const isSky = metaMatch[2].toLowerCase() === 'length' || metaMatch[2].toLowerCase() === 'offset';
          const valClass = isSky ? 'cm-lrc-meta-val-sky' : 'cm-lrc-meta-val';
          return `<span class="cm-lrc-meta-bracket">${escapeHtml(metaMatch[1])}</span><span class="cm-lrc-meta-tag">${escapeHtml(metaMatch[2])}:</span><span class="${valClass}">${escapeHtml(metaMatch[3])}</span><span class="cm-lrc-meta-bracket">${escapeHtml(metaMatch[4])}</span>`;
        }
        return escapeHtml(line);
      });
      previewBox.innerHTML = htmlLines.join('\n');
      previewBox.classList.remove('empty');
    } else {
      previewBox.textContent = '[No metadata tags populated]';
      previewBox.classList.add('empty');
    }
  }
}

/**
 * Updates the metadata count badge on the studio header.
 */
export function syncMetadataBadge(): void {
  const badge = document.getElementById('metadataCountBadge');
  if (!badge) return;

  const rawText = getEditorValue();
  const parsed = parseMetadata(rawText);

  if (parsed.tagCount > 0) {
    badge.style.display = 'inline-flex';
    badge.textContent = String(parsed.tagCount);
  } else {
    badge.style.display = 'none';
  }
}

/**
 * Opens the Metadata Manager modal dialog.
 */
export function openMetadataModal(): void {
  const backdrop = document.getElementById('metadataModalBackdrop');
  if (!backdrop) return;

  // Auto-sync fields from current editor lyrics
  const parsed = parseMetadata(getEditorValue());
  setMetadataToFields(parsed.metadata);

  backdrop.style.display = 'flex';
  backdrop.setAttribute('aria-hidden', 'false');
  document.body.classList.add('modal-open');

  triggerHaptic(12);

  // Focus title field on desktop only (avoid virtual keyboard jump on mobile)
  if (window.innerWidth > 768) {
    const titleField = document.getElementById('meta_ti') as HTMLInputElement | null;
    if (titleField) {
      setTimeout(() => titleField.focus(), 50);
    }
  }
}

/**
 * Closes the Metadata Manager modal dialog.
 */
export function closeMetadataModal(): void {
  const backdrop = document.getElementById('metadataModalBackdrop');
  if (!backdrop) return;

  backdrop.style.display = 'none';
  backdrop.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('modal-open');
}

/**
 * Applies current modal metadata to the active lyrics editor.
 */
export function applyMetadataToEditor(): void {
  const currentText = getEditorValue();

  // Save history snapshot for undo
  setHistorySnapshot({
    lyrics: currentText,
    input: currentText
  });

  const metadata = getMetadataFromFields();
  const updatedText = applyMetadataToLrc(currentText, metadata);
  setEditorValue(updatedText);

  updateCounters();
  syncMetadataBadge();
  closeMetadataModal();
  triggerHaptic(15);

  showToast('✓ Applied metadata tags to lyrics! • <span class="undo-action">Undo</span>');
}

/**
 * Backward compatibility alias for applyMetadataToEditor.
 */
export const applyMetadataToTarget = (_target?: 'input' | 'output') => applyMetadataToEditor();

/**
 * Initializes all event handlers for the Metadata Manager Modal.
 */
export function initMetadataModal(): void {
  const openBtn = document.getElementById('metadataBtn');
  const closeBtn = document.getElementById('closeMetadataBtn');
  const cancelBtn = document.getElementById('cancelMetaBtn');
  const backdrop = document.getElementById('metadataModalBackdrop');
  const modal = document.getElementById('metadataModalCard');

  const applyMetaBtn = document.getElementById('applyMetaBtn');
  const applyInputBtn = document.getElementById('applyMetaToInputBtn');
  const applyOutputBtn = document.getElementById('applyMetaToOutputBtn');
  const syncBtn = document.getElementById('metaSyncBtn');
  const syncInputBtn = document.getElementById('metaSyncInputBtn');
  const syncOutputBtn = document.getElementById('metaSyncOutputBtn');
  const sortBtn = document.getElementById('metaSortBtn');
  const clearFieldsBtn = document.getElementById('metaClearFieldsBtn');

  if (openBtn) openBtn.addEventListener('click', openMetadataModal);
  if (closeBtn) closeBtn.addEventListener('click', closeMetadataModal);
  if (cancelBtn) cancelBtn.addEventListener('click', closeMetadataModal);

  // Close when clicking outside modal card
  if (backdrop && modal) {
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) {
        closeMetadataModal();
      }
    });
  }

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const isVisible = backdrop && backdrop.style.display !== 'none';
      if (isVisible) {
        closeMetadataModal();
      }
    }
  });

  // Live preview updates when typing in fields
  for (const id of FIELD_IDS) {
    const el = document.getElementById(`meta_${id}`);
    if (el) {
      el.addEventListener('input', updateLivePreview);
    }
  }

  // Quick sync action from editor lyrics
  const handleSync = () => {
    const parsed = parseMetadata(getEditorValue());
    setMetadataToFields(parsed.metadata);
    triggerHaptic(10);
    showToast(`✓ Synced ${parsed.tagCount} tags from lyrics`);
  };

  if (syncBtn) syncBtn.addEventListener('click', handleSync);
  if (syncInputBtn) syncInputBtn.addEventListener('click', handleSync);
  if (syncOutputBtn) syncOutputBtn.addEventListener('click', handleSync);

  if (sortBtn) {
    sortBtn.addEventListener('click', () => {
      const meta = getMetadataFromFields();
      setMetadataToFields(meta);
      triggerHaptic(10);
      showToast('✓ Cleaned and organized tags');
    });
  }

  if (clearFieldsBtn) {
    clearFieldsBtn.addEventListener('click', () => {
      setMetadataToFields({});
      triggerHaptic(10);
      showToast('Cleared metadata fields');
    });
  }

  // Apply action
  if (applyMetaBtn) applyMetaBtn.addEventListener('click', applyMetadataToEditor);
  if (applyInputBtn) applyInputBtn.addEventListener('click', applyMetadataToEditor);
  if (applyOutputBtn) applyOutputBtn.addEventListener('click', applyMetadataToEditor);

  // Update badge initially
  syncMetadataBadge();
}
